import {
  AttachMoney,
  Calculate,
  Equalizer,
  RequestQuote,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { getSharedBusinessReport } from 'lib/api/multiverse/fund';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

const DATA_HEADERS = [
  { key: 'segmentName', label: 'Segmento', type: 'money' },
  { key: 'noArrear', label: 'Sin mora', type: 'money' },
  { key: 'days1to30', label: '1-30 días', type: 'money' },
  { key: 'days31to60', label: '31-60 días', type: 'money' },
  { key: 'days61to90', label: '61-90 días', type: 'money' },
  { key: 'days91Plus', label: '91+ días', type: 'money' },
];

const TOGGLE_BUTTON_OPTIONS = [
  { value: 'economic', label: 'Económico', ariaLabel: 'economic' },
  { value: 'sales', label: 'Venta', ariaLabel: 'sales' },
];

type ArrearType = 'economic' | 'sales';

const Report = () => {
  const dispatch = useDispatch();
  const { companyId, businessIdentifier } = useParams();

  if (!companyId || !businessIdentifier) return null;

  const defaultEndDate = dayjs();
  const defaultStartDate = dayjs().subtract(29, 'days');
  const [startDate, setStartDate] = useState<Dayjs>(defaultStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(defaultEndDate);
  const [clientChartType, setClientChartType] =
    useState<ArrearType>('economic');
  const [stakeholderChartType, setStakeholderChartType] =
    useState<ArrearType>('economic');
  const [clientArrearType, setClientArrearType] =
    useState<ArrearType>('economic');
  const [stakeholderArrearType, setStakeholderArrearType] =
    useState<ArrearType>('economic');

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', startDate.format('YYYY-MM-DD'));
    }
    if (endDate) {
      params.append('endDate', endDate.format('YYYY-MM-DD'));
    }
    return params.toString();
  }, [startDate, endDate]);

  const { data, isPending } = getSharedBusinessReport(
    dispatch,
    businessIdentifier,
    companyId,
    query,
  );

  if (isPending) return <CenteredCircularProgress type="layout" />;

  return (
    <Grid container spacing={2} my={1}>
      <Grid item xs={8}>
        <TableCard>
          <SubtitleWithTooltip
            title="Cesión en el tiempo"
            tooltipContent="Estos son los montos de cesión por fuente de financiamiento a lo largo del tiempo."
            tooltipPlacement="right"
            variant="compact"
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha inicio"
                value={startDate}
                onChange={(newValue) => {
                  if (newValue) setStartDate(newValue);
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Fecha término"
                value={endDate}
                onChange={(newValue) => {
                  if (newValue) setEndDate(newValue);
                }}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                  },
                }}
              />
            </LocalizationProvider>
          </SubtitleWithTooltip>
          <LineChart
            key="cessionAmountLineChart"
            data={data?.cessionAmountLineChartData || []}
            showLegend={true}
            height={290}
          />
        </TableCard>
      </Grid>

      <Grid item xs={4}>
        <TableCard>
          <SubtitleWithTooltip
            title="Monto de cesión"
            tooltipContent="Estos son los montos de cesión por fuente de financiamiento"
            tooltipPlacement="right"
            variant="compact"
          />
          <CentralPieChart data={data?.cessionAmountPieChartData || []} />
        </TableCard>
      </Grid>

      <Grid item xs={3}>
        <StatCard
          label="Tasa promedio"
          value={(data?.statistics.averageRate ?? 0).toFixed(2)}
          valueLabel="%"
          color={BASE_COLORS.blue}
          icon={<RequestQuote sx={{ fontSize: 24 }} />}
        />
      </Grid>
      <Grid item xs={3}>
        <StatCard
          label="Promedio cesión"
          value={formatMoney(data?.statistics.averageCessionAmount || 0)}
          valueLabel="CLP"
          color={BASE_COLORS.green}
          icon={<AttachMoney sx={{ fontSize: 24 }} />}
        />
      </Grid>
      <Grid item xs={3}>
        <StatCard
          label="Promedio operación"
          value={formatMoney(
            data?.statistics.averageCessionAmountByNominee || 0,
          )}
          valueLabel="CLP"
          color={BASE_COLORS.purple}
          icon={<Calculate sx={{ fontSize: 24 }} />}
        />
      </Grid>
      <Grid item xs={3}>
        <StatCard
          label="Financiamiento promedio"
          value={data?.statistics.averageAdvance || 0}
          valueLabel="%"
          color={BASE_COLORS.red}
          icon={<Equalizer sx={{ fontSize: 24 }} />}
        />
      </Grid>

      <Grid item xs={6}>
        <TableCard>
          <SubtitleWithTooltip
            title="Clientes"
            tooltipContent="Desglose de cesión por segmento económico o de venta del cliente"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={clientChartType}
              onChange={(value) => setClientChartType(value as ArrearType)}
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <CentralPieChart
            data={
              clientChartType === 'economic'
                ? data?.clientEconomicSegmentPieChartData || []
                : data?.clientSalesSegmentPieChartData || []
            }
            height={240}
          />
        </TableCard>
      </Grid>
      <Grid item xs={6}>
        <TableCard>
          <SubtitleWithTooltip
            title="Deudores"
            tooltipContent="Desglose de cesión por segmento económico o de venta del deudor"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={stakeholderChartType}
              onChange={(value) => setStakeholderChartType(value as ArrearType)}
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <CentralPieChart
            data={
              stakeholderChartType === 'economic'
                ? data?.stakeholderEconomicSegmentPieChartData || []
                : data?.stakeholderSalesSegmentPieChartData || []
            }
            height={240}
          />
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Clientes"
            tooltipContent="Desglose de mora por segmento económico o de venta del cliente"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={clientArrearType}
              onChange={(value) => setClientArrearType(value as ArrearType)}
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <EnhancedTable
            data={
              clientArrearType === 'economic'
                ? data?.clientEconomicSegmentArrearTable || []
                : data?.clientSalesSegmentArrearTable || []
            }
            dataHeaders={DATA_HEADERS}
          />
        </TableCard>
      </Grid>

      <Grid item xs={12}>
        <TableCard>
          <SubtitleWithTooltip
            title="Deudores"
            tooltipContent="Desglose de mora por segmento económico o de venta del deudor"
            tooltipPlacement="right"
            variant="compact"
          >
            <CustomToggleButtonGroup
              value={stakeholderArrearType}
              onChange={(value) =>
                setStakeholderArrearType(value as ArrearType)
              }
              options={TOGGLE_BUTTON_OPTIONS}
            />
          </SubtitleWithTooltip>
          <EnhancedTable
            data={
              stakeholderArrearType === 'economic'
                ? data?.stakeholderEconomicSegmentArrearTable || []
                : data?.stakeholderSalesSegmentArrearTable || []
            }
            dataHeaders={DATA_HEADERS}
          />
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default Report;
